import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  IconButton,
  Button,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import intl from "components/i18n/ReactIntlWrapper";
import { SearchOutlined, CancelOutlined } from "@mui/icons-material";
import { Link } from "react-router";

type UserProfile = {
  entityName: string;
  entityTypeName: string;
};

type GroupedUser = {
  email: string;
  id?: string; //TODO CC: check if this is needed
  profiles: UserProfile[];
};

const AssignedUsers = ({
  users,
  closeModal = null,
  searchedUser,
  setSearchedUser,
  idNumber = null,
}) => {
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [groupedUsers, setGroupedUsers] = useState<Record<number, GroupedUser>>(
    {}
  );

  const groupProfilesByUser = (users: any[]): Record<number, GroupedUser> => {
    return users?.reduce((acc: Record<number, GroupedUser>, user) => {
      const { idUser, email, entityName, entityTypeName } = user;
      if (!acc[idUser]) {
        acc[idUser] = {
          email,
          profiles: [],
        };
      }
      acc[idUser].profiles.push({ entityName, entityTypeName });
      return acc;
    }, {});
  };

  const handleSearchUser = () => {
    const filtered = users.filter((user) =>
      user.entityName.toLowerCase().includes(searchedUser.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    setFilteredUsers(users);
  }, [searchedUser, users]);

  useEffect(() => {
    const grouped = groupProfilesByUser(filteredUsers);
    setGroupedUsers(grouped);
  }, [filteredUsers]);

  return (
    <>
      {idNumber ? (
        <>
          <Grid container spacing={2} alignItems="center" mb={3}>
            <Grid
              size={{
                xs: 12,
                md: 10
              }}>
              <Box display="flex">
                <TextField
                  fullWidth
                  size="small"
                  placeholder={intl.get("assignedUsers.searchBar.placeholder")}
                  value={searchedUser}
                  onChange={(e) => setSearchedUser(e.target.value)}
                  InputProps={{
                    endAdornment: searchedUser && (
                      <IconButton
                        size="small"
                        onClick={() => setSearchedUser("")}
                      >
                        <CancelOutlined />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
            </Grid>
            <Grid
              textAlign="right"
              size={{
                xs: 12,
                md: 2
              }}>
              <Button
                variant="contained"
                startIcon={<SearchOutlined />}
                onClick={handleSearchUser}
              >
                {intl.get("searchBar.button")}
              </Button>
            </Grid>
          </Grid>

          <Grid container className="maxHeight">
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {groupedUsers &&
                    Object.entries(groupedUsers).map(([idUser, user], i) => (
                      <React.Fragment key={i}>
                        <TableRow className="table-success">
                          <TableCell colSpan={2}>
                            <Typography>User - ({user.email})</Typography>
                          </TableCell>
                        </TableRow>
                        {user.profiles.map((profile, j) => (
                          <TableRow key={j}>
                            <TableCell sx={{ pl: 6 }}>
                              {profile.entityName} ({profile.entityTypeName})
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
      ) : (
        <>
          <Grid container>
            <Grid size={12}>
              <Typography variant="h5" mb={2}>
                {intl.get("assignedUsers.title")}
              </Typography>

              <Grid container spacing={2} alignItems="center" mb={3}>
                <Grid
                  size={{
                    xs: 12,
                    md: 10
                  }}>
                  <Box display="flex">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={intl.get(
                        "assignedUsers.searchBar.placeholder"
                      )}
                      value={searchedUser}
                      onChange={(e) => setSearchedUser(e.target.value)}
                      InputProps={{
                        endAdornment: searchedUser && (
                          <IconButton
                            size="small"
                            onClick={() => setSearchedUser("")}
                          >
                            <CancelOutlined />
                          </IconButton>
                        ),
                      }}
                    />
                  </Box>
                </Grid>
                <Grid
                  textAlign="right"
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchOutlined />}
                    onClick={handleSearchUser}
                  >
                    {intl.get("searchBar.button")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container className="maxHeight">
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {Object.entries(groupedUsers).map(([idUser, user], i) => (
                    <React.Fragment key={i}>
                      <TableRow className="table-success">
                        <TableCell colSpan={2}>
                          <Link to={`/user/detail/${idUser}`}>
                            <Typography>{user.email}</Typography>
                          </Link>
                        </TableCell>
                      </TableRow>
                      {user.profiles.map((profile, j) => (
                        <TableRow key={j}>
                          <TableCell sx={{ pl: 6 }}>
                            {profile.entityName} ({profile.entityTypeName})
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button variant="outlined" color="error" onClick={closeModal}>
              {intl.get("close.button")}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default AssignedUsers;
